import { create } from 'zustand';
import { assetApiBase, cdnBase } from './config';
import { currentAccessToken } from '../auth/useAuth';

/**
 * Client signer for *expensive* asset refs (`ai=` / `bits=16`), which the asset
 * service requires to be signed. The SIGN_SECRET lives server-side, so we POST
 * the ref to the Cognito-gated `/sign` route and cache the returned signed URL.
 *
 * Mirrors the objectURL pattern in `assetUrls.ts`: `signedUrl()` is a sync read
 * (null until minted) and the store updates trigger a re-render when the
 * signature lands, so the resolver flips from the optimistic local image to the
 * signed CDN URL on the next paint.
 */

interface SignedState {
  /** canonicalKey → signed CDN url. */
  sigs: Record<string, string>;
}

export const useSignedStore = create<SignedState>(() => ({ sigs: {} }));

/** A ref needs a signature iff it carries an `ai=` or `bits=16` transform. */
export function isExpensiveParams(params: string | undefined): boolean {
  if (!params) return false;
  const q = new URLSearchParams(params);
  return q.has('ai') || q.get('bits') === '16';
}

/** Stable key: id + params sorted exactly like the server canonicalizes them. */
export function canonKey(id: string, params: string): string {
  const parts = [...new URLSearchParams(params).entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`);
  return `${id}?${parts.join('&')}`;
}

/** Sync read for the pure rewriter; null = not yet signed. */
export function signedUrl(id: string, params: string): string | null {
  return useSignedStore.getState().sigs[canonKey(id, params)] ?? null;
}

const inFlight = new Set<string>();

/**
 * Mint + cache signatures for the given expensive refs. Best-effort: on a 401
 * or network failure we leave the ref unsigned (the resolver falls back to the
 * local image) — a later re-render retries once the session token is valid.
 */
export async function ensureSigned(refs: { id: string; params: string }[]): Promise<void> {
  const todo = refs.filter(({ id, params }) => {
    const key = canonKey(id, params);
    return !useSignedStore.getState().sigs[key] && !inFlight.has(key);
  });
  if (todo.length === 0) return;
  await Promise.all(
    todo.map(async ({ id, params }) => {
      const key = canonKey(id, params);
      inFlight.add(key);
      try {
        const token = currentAccessToken();
        const res = await fetch(`${assetApiBase()}/sign`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ path: `/a/${id}`, params }),
        });
        if (!res.ok) return; // leave unsigned; caller falls back, re-render retries
        const { s } = (await res.json()) as { s: string };
        const url = `${cdnBase()}/a/${id}?${params}&s=${s}`;
        useSignedStore.setState((st) => ({ sigs: { ...st.sigs, [key]: url } }));
      } catch {
        // swallow — best-effort
      } finally {
        inFlight.delete(key);
      }
    }),
  );
}
